import { Component, Input, OnInit } from "@angular/core";
import {
  Connector,
  ConnectorWire,
  InterfaceConfiguration,
  InterfaceType,
  Wiring,
} from "../../../../../codegen/out/types";

@Component({
  selector: "app-system-wiring",
  templateUrl: "./system-wiring.component.html",
  styleUrls: ["./system-wiring.component.scss"],
  standalone: false,
})
export class SystemWiringComponent implements OnInit {
  @Input() interfaceConfiguration: InterfaceConfiguration;

  devices: string[];
  wirings: Wiring[];
  devicesWithWiring = new Set();
  connector?: Connector;
  connectorTerminals: ConnectorWire[] = [];
  connectorProperties: ConnectorWire[] = [];
  interfaceActivation: string;
  hasComment: boolean;

  interfaceConnectorConfigs: Map<
    InterfaceType,
    { terminals: string[]; properties: string[] }
  > = new Map([
    [
      InterfaceType.SingleRelay,
      {
        terminals: ["COM", "NO", "NC", "L", "N"],
        properties: ["Channel", "Voltage", "MaximumCurrent"],
      },
    ],
    [
      InterfaceType.DualRelay,
      {
        terminals: ["COM1", "NO1", "NC1", "COM2", "NO2", "NC2", "L", "N"],
        properties: [
          "DualRelayChannel",
          "HwChLink",
          "Voltage",
          "MaximumCurrent",
        ],
      },
    ],
    [
      InterfaceType.TripleRelay,
      {
        terminals: [
          "COM1",
          "NO1",
          "NC1",
          "COM2",
          "NO2",
          "NC2",
          "COM3",
          "NO3",
          "NC3",
          "L",
          "N",
        ],
        properties: [
          "TripleRelayChannel",
          "HwChLink",
          "Voltage",
          "MaximumCurrent",
        ],
      },
    ],
    [
      InterfaceType.QuadrupleRelay,
      {
        terminals: [
          "COM1",
          "NO1",
          "NC1",
          "COM2",
          "NO2",
          "NC2",
          "COM3",
          "NO3",
          "NC3",
          "COM4",
          "NO4",
          "NC4",
          "L",
          "N",
        ],
        properties: ["QuadrupelRelayChannel", "Voltage", "MaximumCurrent"],
      },
    ],
    [
      InterfaceType.Analog,
      {
        terminals: ["AnalogOut_A", "GND_B", "VCC_C"],
        properties: [],
      },
    ],
    [
      InterfaceType.Digital_2Wires,
      {
        terminals: ["GND_B", "VCC_C"],
        properties: [],
      },
    ],
    [
      InterfaceType.Digital_3Wires,
      {
        terminals: ["GND_B", "VCC_C", "RX_TX"],
        properties: [],
      },
    ],
    [
      InterfaceType.Digital_4Wires,
      {
        terminals: ["GND", "VCC", "RX", "TX"],
        properties: [],
      },
    ],
    [
      InterfaceType.UsStandardMultiRelay,
      {
        terminals: [
          "R",
          "C",
          "RH",
          "W",
          "W2",
          "RC",
          "Y",
          "Y2",
          "G",
          "HUM",
          "DEHUM",
          "AUX",
          "OB",
          "E",
        ],
        properties: [],
      },
    ],
  ]);

  ngOnInit(): void {
    const legacyDevices = ["BY01", "BX01", "BX02"];
    this.wirings = this.interfaceConfiguration.wiring.filter(
      (wiring) =>
        !wiring.devices.some((device) => legacyDevices.includes(device))
    );

    this.wirings.forEach((wiring) => {
      wiring.devices.forEach((device) => this.devicesWithWiring.add(device));
    });

    this.devices = this.interfaceConfiguration.phyCompatibleDevices.filter(
      (device) => !legacyDevices.includes(device)
    );

    this.connector = this.interfaceConfiguration.connector;
    if (
      this.connector &&
      this.interfaceConnectorConfigs.has(
        this.interfaceConfiguration.interface.type
      )
    ) {
      const ignoredValues = ["HW-CH-independent"];
      const connectorWires = new Map(
        (this.connector.configuration || [])
          .filter(
            (wire) =>
              wire.value.length > 0 && !ignoredValues.includes(wire.value)
          )
          .map((wire) => [wire.field, wire])
      );
      const connectorConfig = this.interfaceConnectorConfigs.get(
        this.interfaceConfiguration.interface.type
      );
      connectorConfig.terminals.forEach((key) => {
        if (connectorWires.has(key)) {
          this.connectorTerminals.push(connectorWires.get(key));
        }
      });
      connectorConfig.properties.forEach((key) => {
        if (connectorWires.has(key)) {
          this.connectorProperties.push(connectorWires.get(key));
        }
      });
    }
    this.interfaceActivation = this.getInterfaceActivationLabel(
      this.interfaceConfiguration.interfaceActivation
    );

    this.hasComment =
      this.interfaceConfiguration.comment != null &&
      this.interfaceConfiguration.comment.length !== 0;
  }

  deviceAvatar(device: string): string {
    if (this.hasWiring(device)) {
      return "cable";
    } else {
      return "unpublished";
    }
  }

  hasWiring(device: string): boolean {
    return this.devicesWithWiring.has(device);
  }

  hasWiringComment(wiring: Wiring): boolean {
    return wiring.comment != null && wiring.comment.length !== 0;
  }

  getInterfaceActivationLabel(interfaceActivation: string): string {
    switch (interfaceActivation) {
      case "BC_RELAY_BRIDGED_REQUIRED":
        return "BC relay needs to be bridged";
      case "BC_RELAY_BRIDGED_POSSIBLE":
        return "BC relay may not be bridged";
      case "NONE":
        return "None";
      case "UNKNOWN":
        return "Unknown";
      case "OTHER":
        return "Other (see comment)";
      default:
        return "-";
    }
  }

  getConnectorPropertyLabel(key: string): string {
    switch (key) {
      case "DualRelayChannel":
        return "Channel";
      case "MaximumCurrent":
        return "Max. Current";
      case "HwChLink":
        return "Gravity fed";
      case "AnalogOut_A":
        return "A";
      case "GND_B":
        return "GND";
      case "VCC_C":
        return "VCC";
      default:
        return key;
    }
  }

  formatConnectorPropertyValue(key: string, value: string): string {
    switch (key) {
      case "MaximumCurrent":
        return `${value}A`;
      case "HwChLink":
        if (value === "HW-CH-configurable") {
          return "Configurable";
        } else if (value === "HW-CH-fixed") {
          return "Fixed";
        } else {
          return value;
        }
      default:
        return value;
    }
  }
}
